<template>
  <div class="markdown-content" v-html="rendered" @click="onLinkClick" />
</template>

<script setup>
import { computed } from 'vue'
import { useMarkdown } from '../../composables/useMarkdown.js'
import { useParentBridge } from '../../composables/useParentBridge.js'

const props = defineProps({
  content: { type: String, default: '' },
})

const { render } = useMarkdown()
const rendered = computed(() => render(props.content))

// When the parent bridge is active (iframe deployments), route in-message
// link clicks through postMessage so the parent SPA can navigate same-origin
// internal routes instead of forcing a new tab from inside the iframe.
const bridge = useParentBridge()

function onLinkClick(event) {
  if (!bridge.isActive || !bridge.interceptLinks) return
  const anchor = event.target.closest('a[href]')
  if (!anchor) return
  const href = anchor.getAttribute('href')
  if (!href) return

  event.preventDefault()
  event.stopPropagation()

  // Same-origin links route into the host SPA's router; external links and
  // bridge failures fall back to a new tab (all handled inside navigate()).
  bridge.navigate(href)
}
</script>

<style>
.markdown-content {
  line-height: 1.6;
  word-break: break-word;
}
/* Cap heading sizes inside chat bubbles. Browser defaults (h1 2em / h2 1.5em) are
   shouty at bubble scale — and an accidental setext heading (a paragraph glued onto
   a "---" or a stray "-" by link stripping) should degrade gracefully, not render
   the paragraph huge. */
.markdown-content h1 {
  font-size: 1.25em;
  margin: 0.6em 0 0.35em;
}
.markdown-content h2 {
  font-size: 1.15em;
  margin: 0.6em 0 0.35em;
}
.markdown-content h3 {
  font-size: 1.08em;
  margin: 0.5em 0 0.3em;
}
.markdown-content h4,
.markdown-content h5,
.markdown-content h6 {
  font-size: 1em;
  margin: 0.5em 0 0.3em;
}
.markdown-content h1:first-child,
.markdown-content h2:first-child,
.markdown-content h3:first-child {
  margin-top: 0;
}
.markdown-content p {
  margin-bottom: 0.5em;
}
.markdown-content p:last-child {
  margin-bottom: 0;
}
.markdown-content pre {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 12px;
  overflow-x: auto;
  margin: 8px 0;
}
.v-theme--dark .markdown-content pre {
  background: rgba(255, 255, 255, 0.05);
}
.markdown-content code {
  font-size: 0.875em;
  font-family: 'Fira Code', 'Consolas', monospace;
}
.markdown-content :not(pre) > code {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
  padding: 2px 6px;
}
.v-theme--dark .markdown-content :not(pre) > code {
  background: rgba(255, 255, 255, 0.08);
}
.markdown-content ul, .markdown-content ol {
  margin: 4px 0;
  padding-left: 20px;
}
.markdown-content table {
  border-collapse: collapse;
  margin: 8px 0;
  width: 100%;
}
.markdown-content th, .markdown-content td {
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 6px 12px;
  text-align: left;
}
.v-theme--dark .markdown-content th,
.v-theme--dark .markdown-content td {
  border-color: rgba(255, 255, 255, 0.12);
}
.markdown-content th {
  font-weight: 600;
}
.markdown-content blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.2);
  margin: 8px 0;
  padding: 4px 12px;
  color: rgba(0, 0, 0, 0.6);
}
.v-theme--dark .markdown-content blockquote {
  border-left-color: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.6);
}

/* Inline per-item link chips, expanded from [[rdrive:issue:…]] / [[rdrive:process:…]] /
   [[rdrive:task:…]] tokens by the markdown renderer. Neutral, label-style (rectangular)
   chips that adapt to the theme — matched to Vuetify's `<v-chip variant="tonal" label>`
   look. */
.markdown-content a.rdrive-item-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 0.8125em;
  font-weight: 500;
  line-height: 1.6;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.78);
  background: rgba(0, 0, 0, 0.08);
  transition: background-color 0.12s ease;
}
.markdown-content a.rdrive-item-chip:hover {
  background: rgba(0, 0, 0, 0.14);
}
.v-theme--dark .markdown-content a.rdrive-item-chip {
  color: rgba(255, 255, 255, 0.82);
  background: rgba(255, 255, 255, 0.10);
}
.v-theme--dark .markdown-content a.rdrive-item-chip:hover {
  background: rgba(255, 255, 255, 0.16);
}
</style>
